import { AppAction, AppContext } from "helpers/context";
import { MapContext } from "helpers/map";
import { useContext, useEffect, useRef } from "react";
import { EventData } from "helpers/models";
import { LeftPanel, RightPanel } from "helpers/constants";
import { getEventsLegends } from "api/layer";
import { cache } from "containers/events/tc-track/marker/point";


const EventsMultipleMarker = () => {
  const [context, dispatch] = useContext(AppContext);
  const { AMap, map } = useContext(MapContext);
  let cluster: any = useRef(null);
  let gridSize = 150;

  const handleMarkerClick = (e: any) => {
    if (e.clusterData.length > 1) {
      handleClusterClick(e);
    } else {
      handleSingleMarkerClick(e);
    }
  };

  const handleClusterClick = (e: any) => {
    map.setCenter([e.lnglat.lng, e.lnglat.lat]);
    map.zoomIn();
  };

  const handleSingleMarkerClick = (e: any) => {
    let data = e.clusterData[0] ? e.clusterData[0] : e.cluster.p;
    if (isEventLimitExceeded(data)) return;

    if (!data.checked) {
      scrollToEvent(data.id);
    }

    updateContextForSingleMarker(data);
  };

  const isEventLimitExceeded = (data: any) => {
    if (context.selectedEventsList.length >= 5 && !data.checked) {
      dispatch({
        type: AppAction.addSnackBarParams,
        payload: {
          title: "eventsLimitAlert",
          titleIcon: "error",
          titleIconColor: "#fff"
        }
      });
      return true;
    }
    return false;
  };

  const scrollToEvent = (eventId: string) => {
    const element = document.getElementById(eventId);
    const container = document.getElementById("events-list-container");
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const containerRect = container && container.getBoundingClientRect();

      if (
        containerRect &&
        (elementRect.bottom < containerRect.top + 41 ||
          elementRect.top > containerRect.bottom - 56)
      ) {
        container.scrollTop = element.offsetTop - 100;
      }
    }
  };

  const updateContextForSingleMarker = (data: any) => {
    dispatch({ type: AppAction.setSearchMapCenter, payload: undefined });
    dispatch({ type: AppAction.setSingleLocationAnalysed, payload: false });
    dispatch({
      type: AppAction.setCurrentMultipleLocationInfo,
      payload: undefined
    });
    data.checked || toggleLocation(data);
    dispatch({ type: AppAction.setCurrentEventsLocationInfo, payload: data });
    dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Events });
  };

  const toggleLocation = (item: any) => {
    let data = context.eventsList?.map((i: EventData) => {
      if (i.id === item.id) {
        i.checked = true;
        const isCross180 =
          i.extent.minimumLongitude > i.extent.maximumLongitude;
        const southWest = new AMap.LngLat(
          i.extent.minimumLongitude,
          i.extent.minimumLatitude,
          isCross180
        );
        const northEast = new AMap.LngLat(
          isCross180
            ? i.extent.maximumLongitude + 360
            : i.extent.maximumLongitude,
          i.extent.maximumLatitude,
          isCross180
        );
        let myBounds = new AMap.Bounds(southWest, northEast);
        map?.setBounds(myBounds, true, [20, 20, 20, 20]);
        if (i.layerObj) {
          i.layerObj.show();
          dispatch({ type: AppAction.updateEventsLegend, payload: i });
        } else {
          let layerObj = new AMap.TileLayer({
            tileUrl: `${
              (window as any).amapLayerUrl
            }/risklayer-api/catnet/tiles/${
              i.layerId
            }/tile/[z]/[x]/[y]?X-Token=${sessionStorage.getItem("token")}`,
            opacity: i.opacity
          });
          i.layerObj = layerObj;
          map.addLayer(layerObj);
          getEventsLegends({ layers: i.layerId }).then(async (res: any) => {
            if (res.legends && res.legends.length > 0) {
              dispatch({
                type: AppAction.addEventsLegend,
                payload: { ...res.legends[0], layerInfo: i }
              });
            }
          });
        }
        dispatch({
          type: AppAction.setSelectedEventsList,
          payload: [...context.selectedEventsList, i]
        });
        dispatch({ type: AppAction.setSearchMapCenter, payload: undefined });
      }
      return i;
    });
    dispatch({ type: AppAction.setEventsList, payload: data });
  };

  const _renderClusterMarker = (event: any) => {
    let content = `<div 
            style="
                font-size:14px;
                font-weight: 500;
                color:#fff;
                border: 4px solid #fff;
                border-radius:50%;
                box-shadow:0px 0px 5px 0px rgba(0, 0, 0, 0.3);
                background: rgba(0,0,0,0.6);
                width:60px;
                height:60px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: space-evenly;
            ">
            <img src="/events/event-cluster.png" style="width:24px;height:24px;"></img>
        ${event.count}
        </div>`;
    event.marker.setContent(content);
  };

  // const infoWindow = new AMap.InfoWindow({
  //   offset: new AMap.Pixel(0, -10), // 信息窗体相对于点的偏移量
  //   closeWhenClickMap: true, // 点击地图关闭窗体
  //   autoMove: true, // 自动调整窗体到视野内
  //   isCustom: true
  // });

  /**
   * 获取坐标点附近最近的标记
   * @param lnglat 参考坐标点
   * @param markers 标记数组
   * @returns 最近的标记
   */
  function getNearestMarker(
    lnglat: AMap.LngLat,
    markers: AMap.CircleMarker[]
  ): AMap.CircleMarker | null {
    if (!markers || markers.length === 0) {
      return null;
    }

    let nearestMarker = null;
    let minDistance = Infinity;

    // 遍历所有标记
    markers.forEach((marker) => {
      // 获取标记中心点
      const center = marker.getCenter();

      // 计算地理距离（单位：米）
      const distance = AMap.GeometryUtil.distance(
        [lnglat.getLng(), lnglat.getLat()],
        [center.getLng(), center.getLat()]
      );

      // 更新最近的标记
      if (distance < minDistance) {
        minDistance = distance;
        nearestMarker = marker;
      }
    });

    return nearestMarker;
  }

  const _renderMarker = (event: any) => {
    let arr = [...(context.eventsList || []), ...context.selectedEventsList];
    let data: any = arr.find((item: EventData) => item.id === event.data[0].id);
    let type =
      data.type === "Cyclone" || data.type === "Winterstorm"
        ? "Storm"
        : data.type;
    let content = `<img src="/events/${type}-${
      data.checked ? "hover" : "default"
    }.png" style="width:100%;height:100%;"></img>`;
    event.marker.setSize(
      data.checked ? new AMap.Size(51, 54) : new AMap.Size(34, 36)
    );
    event.marker.setOffset(
      data.checked ? new AMap.Pixel(-25, -27) : new AMap.Pixel(-17, -18)
    );
    event.marker.setContent(content);
    // event.marker.setOptions({
    //     bubble: true
    // });
    let nearestMarker: AMap.CircleMarker | null;
    event.marker.on("mouseover", function (e: any) {
      const pointCache: AMap.CircleMarker[] = cache[data.id];
      // 获取最近的标记
      nearestMarker = getNearestMarker(e.lnglat, pointCache);
      if (nearestMarker) {
        // 创建模拟事件对象
        const center = nearestMarker.getCenter()
        const simulatedEvent = {
          lnglat: center,
          target: nearestMarker,
          pixel: map.lngLatToContainer(center)
        };
        
        // 触发该标记的mouseover事件
        if (typeof nearestMarker.emit === 'function') {
          nearestMarker.emit('mouseover', simulatedEvent);
        }
      }
    });

    event.marker.on("mouseout", function () {
      // 关闭信息窗体
      if (nearestMarker) {
        // 创建模拟事件对象
        const center = nearestMarker.getCenter()
        const simulatedEvent = {
          lnglat: center,
          target: nearestMarker,
          pixel: map.lngLatToContainer(center)
        };
        
        // 触发该标记的mouseout事件
        if (typeof nearestMarker.emit === 'function') {
          nearestMarker.emit('mouseout', simulatedEvent);
        }
      }
    });
  };

  useEffect(() => {
    if (
      AMap &&
      ((context.eventsList && context.eventsList.length > 0) ||
        (context.selectedEventsList && context.selectedEventsList.length > 0))
    ) {
      // 在这里编写组件挂载时的逻辑

      let points: any = [],
        arr = [...(context.eventsList || []), ...context.selectedEventsList];
      arr = arr.filter(
        (item: EventData, index) =>
          arr.findIndex((i: EventData) => i.id === item.id) === index
      ); // 去重
      // 数据中需包含经纬度信息字段 lnglat
      arr.forEach((item: EventData) => {
        if (context.eventsFilterForm?.type?.includes(item.type)) {
          if (context.leftPanel === LeftPanel.Events) {
            points.push({
              ...item,
              lnglat: [item.longitude, item.latitude]
            });
          } else if (item.checked) {
            points.push({ ...item, lnglat: [item.longitude, item.latitude] });
          }
        } else {
          let isFind = context.selectedEventsList.find(
            (i: EventData) => i.id === item.id
          );
          if (isFind) {
            points.push({ ...item, lnglat: [item.longitude, item.latitude] });
          }
        }
      });

      cluster.current = new AMap.MarkerCluster(map, points, {
        bubble: true,
        gridSize: gridSize, // 设置网格像素大小
        renderClusterMarker: _renderClusterMarker, // 自定义聚合点样式
        renderMarker: _renderMarker, // 自定义非聚合点样式
        maxZoom: context.leftPanel === LeftPanel.Events ? 4.06 : 1 // 最大显示级别1000公里
      });

      cluster.current.on("click", handleMarkerClick);
    }
    return () => {
      // 在这里编写组件卸载时的逻辑
      if (cluster.current) {
        cluster.current.off("click", handleMarkerClick);
        cluster.current.setMap(null);
        cluster.current = null;
      }
    };
  }, [context.eventsList, context.leftPanel, context.eventsFilterForm]);

  return <div></div>;
}

export default EventsMultipleMarker;